import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Phone, Mail, MessageCircle, Calendar, Clock, FileText, Pencil, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const ActivityIcon = ({ type }) => {
  switch (type) {
    case 'call': return <Phone className="h-4 w-4 text-blue-600" />;
    case 'email': return <Mail className="h-4 w-4 text-green-600" />;
    case 'meeting': return <Calendar className="h-4 w-4 text-purple-600" />;
    case 'note': return <FileText className="h-4 w-4 text-amber-600" />;
    default: return <MessageCircle className="h-4 w-4 text-slate-500" />;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const ActivityItem = ({ activity, onEdit, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editNote, setEditNote] = useState(activity.description);

  const handleSave = () => {
    onEdit(activity.id, { description: editNote });
    setIsEditing(false);
  };

  return (
    <div className="relative group">
      {/* Dot indicador */}
      <div className="absolute -left-[29px] top-1 bg-white border-2 border-indigo-100 rounded-full p-1 z-10">
        <ActivityIcon type={activity.activity_type} />
      </div>
      
      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 hover:border-indigo-100 transition-colors">
        <div className="flex justify-between items-start mb-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {activity.activity_type === 'call' ? 'Llamada' : 
             activity.activity_type === 'email' ? 'Correo' : 
             activity.activity_type === 'meeting' ? 'Reunión' : 'Nota'}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">
              {formatDate(activity.activity_date || activity.created_at)}
            </span>
            
            {/* Botones de Acción (Visibles en Hover) */}
            {!isEditing && (
              <div className="hidden group-hover:flex gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 text-slate-400 hover:text-indigo-600"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil size={12} />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 text-slate-400 hover:text-red-600"
                  onClick={() => onDelete(activity.id)}
                >
                  <Trash2 size={12} />
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {isEditing ? (
          <div className="mt-2 space-y-2">
            <Textarea 
              value={editNote} 
              onChange={(e) => setEditNote(e.target.value)} 
              className="text-sm bg-white min-h-[80px]"
            />
            <div className="flex justify-end gap-2">
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setIsEditing(false)}
                className="h-7 text-xs"
              >
                Cancelar
              </Button>
              <Button 
                size="sm" 
                onClick={handleSave}
                className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
              >
                Guardar
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-700 whitespace-pre-wrap">
            {activity.description}
          </p>
        )}
        
        {activity.outcome && (
          <div className="mt-2 flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              activity.outcome === 'completed' ? 'bg-green-100 text-green-700' :
              activity.outcome === 'no_answer' ? 'bg-orange-100 text-orange-700' :
              'bg-slate-200 text-slate-600'
            }`}>
              {activity.outcome === 'completed' ? 'Completada' :
               activity.outcome === 'no_answer' ? 'No contestó' :
               activity.outcome === 'voicemail' ? 'Buzón' : activity.outcome}
            </span>
            {activity.duration_minutes > 0 && (
              <span className="text-xs text-slate-400">
                • {activity.duration_minutes} min
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const ClientActivityTimeline = ({ activities, loading, onUpdateActivity, onDeleteActivity }) => {
  const [deleteId, setDeleteId] = useState(null);

  const confirmDelete = () => {
    if (deleteId) {
      onDeleteActivity(deleteId);
      setDeleteId(null);
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-slate-500">Cargando historial...</div>;
  }

  if (!activities || activities.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5 text-indigo-600" />
            Historial de Actividad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200">
            <MessageCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p>No hay actividades registradas</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5 text-indigo-600" />
            Historial de Actividad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative pl-4 border-l-2 border-slate-100 space-y-6 ml-2">
            {activities.map((activity, index) => (
              <ActivityItem 
                key={activity.id || index} 
                activity={activity} 
                onEdit={onUpdateActivity}
                onDelete={setDeleteId}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar actividad?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
